(() => {
  const normalise = (value) => String(value || '')
    .toLocaleLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

  const safeGuideUrl = (value) => {
    try {
      const raw = String(value || '').trim();
      if (!raw) return '';
      const url = new URL(raw, window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
    } catch (_error) {
      return '';
    }
  };

  const initialiseLibrary = (library) => {
    if (!library || library.dataset.toolLibraryReady === 'true') return;
    library.dataset.toolLibraryReady = 'true';

    const search = library.querySelector('[data-tool-search]');
    const clear = library.querySelector('[data-tool-search-clear]');
    const status = library.querySelector('[data-tool-search-status]');
    const empty = library.querySelector('[data-tool-empty]');
    const entries = [...library.querySelectorAll('[data-tool-entry]')];
    const guideRequests = [...library.querySelectorAll('[data-guide-request]')];
    const dialog = library.querySelector('[data-guide-dialog]');
    const closeButton = library.querySelector('[data-guide-close]');
    const form = library.querySelector('.tool-guide-form');
    const formTitle = library.querySelector('[data-guide-form-title]');
    const note = library.querySelector('[data-guide-note]');
    const unlock = library.querySelector('[data-guide-unlock]');
    const download = library.querySelector('[data-guide-download]');
    const successTitle = library.querySelector('[data-guide-success-title]');
    const successCopy = library.querySelector('[data-guide-success-copy]');
    const successFallback = library.querySelector('[data-guide-success-fallback]');
    const storageKey = library.dataset.storageKey || 'qt4u-guide';
    let selectedGuide = null;
    let opener = null;

    const readSelection = () => {
      try {
        const value = JSON.parse(window.sessionStorage.getItem(storageKey) || 'null');
        if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
        const url = safeGuideUrl(value && value.url);
        const requestButton = guideRequests.find((button) => safeGuideUrl(button.dataset.guideUrl) === url);
        if (!url || !requestButton) return null;
        return {
          url,
          title: String(requestButton.dataset.guideTitle || 'product'),
          code: String(requestButton.dataset.guideCode || '')
        };
      } catch (_error) {
        return null;
      }
    };

    const saveSelection = (selection) => {
      selectedGuide = selection;
      try {
        window.sessionStorage.setItem(storageKey, JSON.stringify(selection));
      } catch (_error) {
        // The form still works if browser storage is unavailable.
      }
    };

    const syncSelectionToForm = (selection) => {
      if (!selection) return;
      if (formTitle) formTitle.textContent = `${selection.title} (${selection.code})`;
      if (note) note.value = `Guide requested: ${selection.title} (${selection.code})`;
    };

    const openDialog = () => {
      if (!dialog) return;
      if (typeof dialog.showModal === 'function') {
        if (!dialog.open) dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
    };

    const closeDialog = () => {
      if (!dialog) return;
      if (typeof dialog.close === 'function') dialog.close();
      else {
        dialog.removeAttribute('open');
        if (opener && document.contains(opener)) opener.focus();
      }
    };

    const renderUnlockedGuide = (selection) => {
      if (!unlock || !selection || !download) return false;
      download.href = selection.url;
      download.textContent = `Open ${selection.title} guide`;
      download.hidden = false;
      if (successTitle) successTitle.textContent = `Your ${selection.title} guide is ready`;
      if (successCopy) successCopy.textContent = 'Open the guide below. It has not been emailed automatically.';
      if (successFallback) successFallback.hidden = true;
      return true;
    };

    const updateResults = () => {
      const query = normalise(search && search.value);
      const terms = query.split(/\s+/).filter(Boolean);
      let matches = 0;

      entries.forEach((entry) => {
        const haystack = normalise(entry.dataset.toolSearchText);
        const visible = terms.length === 0 || terms.every((term) => haystack.includes(term));
        entry.hidden = !visible;
        if (visible) matches += 1;
      });

      if (clear) clear.hidden = query.length === 0;
      if (empty) empty.hidden = matches !== 0;
      if (status) {
        const noun = matches === 1 ? 'product guide' : 'product guides';
        status.textContent = query
          ? `${matches} ${noun} found for ${search.value.trim()}.`
          : `Showing all ${matches} ${noun}.`;
      }
    };

    if (search) {
      search.addEventListener('input', updateResults);
      search.addEventListener('search', updateResults);
      updateResults();
    }

    if (clear) {
      clear.addEventListener('click', () => {
        search.value = '';
        updateResults();
        search.focus();
      });
    }

    guideRequests.forEach((button) => {
      button.addEventListener('click', () => {
        const url = safeGuideUrl(button.dataset.guideUrl);
        if (!url) return;
        opener = button;
        const selection = {
          url,
          title: button.dataset.guideTitle || 'product',
          code: button.dataset.guideCode || ''
        };
        saveSelection(selection);
        syncSelectionToForm(selection);
        renderUnlockedGuide(selection);
        openDialog();
      });
    });

    if (form) {
      form.addEventListener('submit', (event) => {
        const selection = selectedGuide || readSelection();
        if (!selection) {
          event.preventDefault();
          closeDialog();
          return;
        }
        saveSelection(selection);
        syncSelectionToForm(selection);
      });
    }

    if (closeButton) closeButton.addEventListener('click', closeDialog);

    if (dialog) {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) closeDialog();
      });
      dialog.addEventListener('close', () => {
        if (opener && document.contains(opener)) opener.focus();
      });
    }

    selectedGuide = readSelection();
    syncSelectionToForm(selectedGuide);

    if (unlock) {
      if (!renderUnlockedGuide(selectedGuide) && successFallback) {
        successFallback.hidden = false;
      }
      openDialog();
      window.requestAnimationFrame(() => unlock.focus());
    } else if (library.querySelector('[data-guide-form-error]') && selectedGuide) {
      openDialog();
    }
  };

  const initialiseAll = (root = document) => {
    root.querySelectorAll('[data-tool-library]').forEach(initialiseLibrary);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll());
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
